<template>
  <div class="app-content pt-3 p-md-3 p-lg-4">
    <div class="container-fluid">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="app-page-title mb-0">Access Control</h1>
      </div>

      <b-tabs content-class="mt-3" nav-class="mb-0">
        <!-- Access Requests Tab -->
        <b-tab title="Access Requests" active>
          <div class="app-card shadow-sm mb-4">
            <div class="app-card-body p-4">
              <div class="row mb-3">
                <div class="col-md-4">
                  <b-form-input
                    v-model="requestFilter"
                    placeholder="Search requests..."
                    debounce="300"
                  ></b-form-input>
                </div>
                <div class="col-md-3">
                  <b-form-select v-model="statusFilter" @change="loadAccessRequests">
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="denied">Denied</option>
                  </b-form-select>
                </div>
                <div class="col-md-5 text-right">
                  <button class="btn btn-outline-primary btn-sm" @click="loadAccessRequests" :disabled="loadingRequests">
                    <b-spinner small v-if="loadingRequests"></b-spinner>
                    <b-icon v-else icon="arrow-clockwise"></b-icon> Refresh
                  </button>
                </div>
              </div>

              <div v-if="loadingRequests" class="text-center my-4">
                <b-spinner variant="primary"></b-spinner>
                <div class="mt-2 text-muted">Loading requests...</div>
              </div>

              <b-table
                v-else
                :items="accessRequests"
                :fields="requestFields"
                :filter="requestFilter"
                :per-page="perPage"
                :current-page="requestPage"
                responsive
                striped
                hover
                show-empty
              >
                <template #empty>
                  <div class="text-center text-muted py-4">No access requests found.</div>
                </template>
                <template #cell(index)="data">
                  {{ (requestPage - 1) * perPage + data.index + 1 }}
                </template>
                <template #cell(status)="data">
                  <b-badge :variant="data.value === 'pending' ? 'warning' : 'danger'">
                    {{ data.value }}
                  </b-badge>
                </template>
                <template #cell(created_at)="data">
                  {{ formatDate(data.value) }}
                </template>
                <template #cell(actions)="data">
                  <div class="btn-group btn-group-sm">
                    <button
                      class="btn btn-success"
                      @click="openApproveModal(data.item)"
                      title="Approve"
                    >
                      <b-icon icon="check-circle"></b-icon>
                    </button>
                    <button
                      class="btn btn-warning"
                      @click="denyRequest(data.item)"
                      v-if="data.item.status !== 'denied'"
                      title="Deny"
                    >
                      <b-icon icon="x-circle"></b-icon>
                    </button>
                    <button
                      class="btn btn-danger"
                      @click="deleteRequest(data.item)"
                      title="Delete"
                    >
                      <b-icon icon="trash"></b-icon>
                    </button>
                  </div>
                </template>
              </b-table>

              <div class="d-flex justify-content-between align-items-center" v-if="accessRequests.length > perPage">
                <small class="text-muted">{{ accessRequests.length }} requests</small>
                <b-pagination
                  v-model="requestPage"
                  :total-rows="accessRequests.length"
                  :per-page="perPage"
                  size="sm"
                ></b-pagination>
              </div>
            </div>
          </div>
        </b-tab>

        <!-- Admin Users Tab -->
        <b-tab title="Admin Users">
          <div class="app-card shadow-sm mb-4">
            <div class="app-card-body p-4">
              <div class="row mb-3">
                <div class="col-md-4">
                  <b-form-input
                    v-model="userFilter"
                    placeholder="Search admin users..."
                    debounce="300"
                  ></b-form-input>
                </div>
                <div class="col-md-8 text-right">
                  <button class="btn btn-outline-primary btn-sm" @click="loadAdminUsers" :disabled="loadingUsers">
                    <b-spinner small v-if="loadingUsers"></b-spinner>
                    <b-icon v-else icon="arrow-clockwise"></b-icon> Refresh
                  </button>
                </div>
              </div>

              <div v-if="loadingUsers" class="text-center my-4">
                <b-spinner variant="primary"></b-spinner>
                <div class="mt-2 text-muted">Loading admin users...</div>
              </div>

              <b-table
                v-else
                :items="adminUsers"
                :fields="userFields"
                :filter="userFilter"
                :per-page="perPage"
                :current-page="userPage"
                responsive
                striped
                hover
                show-empty
              >
                <template #empty>
                  <div class="text-center text-muted py-4">No admin users found.</div>
                </template>
                <template #cell(index)="data">
                  {{ (userPage - 1) * perPage + data.index + 1 }}
                </template>
                <template #cell(roles)="data">
                  <b-badge
                    v-for="role in data.value"
                    :key="role"
                    variant="info"
                    class="mr-1"
                  >{{ role }}</b-badge>
                  <span v-if="!data.value || data.value.length === 0" class="text-muted">No roles</span>
                </template>
                <template #cell(actions)="data">
                  <div class="btn-group btn-group-sm">
                    <button
                      class="btn btn-primary"
                      @click="openEditRolesModal(data.item)"
                      title="Edit Roles"
                    >
                      <b-icon icon="pencil"></b-icon>
                    </button>
                    <button
                      class="btn btn-danger"
                      @click="deleteAdminUser(data.item)"
                      title="Delete User"
                    >
                      <b-icon icon="trash"></b-icon>
                    </button>
                  </div>
                </template>
              </b-table>

              <div class="d-flex justify-content-between align-items-center" v-if="adminUsers.length > perPage">
                <small class="text-muted">{{ adminUsers.length }} admin users</small>
                <b-pagination
                  v-model="userPage"
                  :total-rows="adminUsers.length"
                  :per-page="perPage"
                  size="sm"
                ></b-pagination>
              </div>
            </div>
          </div>
        </b-tab>
      </b-tabs>

      <!-- Approve Request Modal -->
      <b-modal
        id="approve-modal"
        title="Approve Access Request"
        @ok="approveRequest"
        ok-title="Approve"
        ok-variant="success"
        :ok-disabled="selectedRoles.length === 0 || approving"
      >
        <div v-if="selectedRequest">
          <div class="mb-3">
            <strong>Name:</strong> {{ selectedRequest.staff_name }}<br>
            <strong>Email:</strong> {{ selectedRequest.email }}<br>
            <strong>Department:</strong> {{ selectedRequest.department }}<br>
            <strong>Staff ID:</strong> {{ selectedRequest.staff_id || 'N/A' }}
          </div>
          <hr>
          <div>
            <label class="font-weight-bold">Assign Roles:</label>
            <div v-if="loadingRoles" class="text-center my-2">
              <b-spinner small variant="primary"></b-spinner>
            </div>
            <div v-else>
              <b-form-checkbox-group
                v-model="selectedRoles"
                :options="availableRoles"
                stacked
              ></b-form-checkbox-group>
            </div>
          </div>
          <div v-if="approving" class="text-center mt-3">
            <b-spinner small variant="success"></b-spinner> Approving...
          </div>
        </div>
      </b-modal>

      <!-- Edit Roles Modal -->
      <b-modal
        id="edit-roles-modal"
        title="Edit User Roles"
        @ok="updateUserRoles"
        ok-title="Update Roles"
        :ok-disabled="editRoles.length === 0 || updatingRoles"
      >
        <div v-if="selectedUser">
          <div class="mb-3">
            <strong>Name:</strong> {{ selectedUser.name }}<br>
            <strong>Email:</strong> {{ selectedUser.email }}
          </div>
          <hr>
          <div>
            <label class="font-weight-bold">Roles:</label>
            <div v-if="loadingRoles" class="text-center my-2">
              <b-spinner small variant="primary"></b-spinner>
            </div>
            <div v-else>
              <b-form-checkbox-group
                v-model="editRoles"
                :options="availableRoles"
                stacked
              ></b-form-checkbox-group>
            </div>
          </div>
          <div v-if="updatingRoles" class="text-center mt-3">
            <b-spinner small variant="primary"></b-spinner> Updating...
          </div>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      accessRequests: [],
      adminUsers: [],
      availableRoles: [],
      loadingRequests: false,
      loadingUsers: false,
      loadingRoles: false,
      approving: false,
      updatingRoles: false,
      requestFilter: "",
      userFilter: "",
      statusFilter: "",
      perPage: 15,
      requestPage: 1,
      userPage: 1,
      selectedRequest: null,
      selectedRoles: [],
      selectedUser: null,
      editRoles: [],
      requestFields: [
        { key: "index", label: "#", class: "text-center", thStyle: { width: "50px" } },
        { key: "staff_name", label: "Name", sortable: true },
        { key: "email", label: "Email", sortable: true },
        { key: "department", label: "Department", sortable: true },
        { key: "staff_id", label: "Staff ID", sortable: true },
        { key: "status", label: "Status", class: "text-center", sortable: true },
        { key: "created_at", label: "Requested", sortable: true },
        { key: "actions", label: "Actions", class: "text-center", thStyle: { width: "140px" } },
      ],
      userFields: [
        { key: "index", label: "#", class: "text-center", thStyle: { width: "50px" } },
        { key: "name", label: "Name", sortable: true },
        { key: "email", label: "Email", sortable: true },
        { key: "roles", label: "Roles", sortable: false },
        { key: "actions", label: "Actions", class: "text-center", thStyle: { width: "120px" } },
      ],
    };
  },
  created() {
    this.loadAccessRequests();
    this.loadAdminUsers();
    this.loadRoles();
  },
  methods: {
    loadAccessRequests() {
      this.loadingRequests = true;
      let url = "admin/access-requests";
      if (this.statusFilter) url += "?status=" + this.statusFilter;
      axios
        .get(url)
        .then((res) => {
          this.accessRequests = res.data.data || [];
        })
        .catch(() => Toast.fire({ icon: "error", title: "Failed to load access requests" }))
        .finally(() => (this.loadingRequests = false));
    },

    loadAdminUsers() {
      this.loadingUsers = true;
      axios
        .get("admin/admin-users")
        .then((res) => {
          this.adminUsers = (res.data.data || []).map((u) => ({
            ...u,
            name: [u.title, u.firstname, u.surname].filter(Boolean).join(" "),
            roles: (u.roles || []).map((r) => r.role || r.name || r),
          }));
        })
        .catch(() => Toast.fire({ icon: "error", title: "Failed to load admin users" }))
        .finally(() => (this.loadingUsers = false));
    },

    loadRoles() {
      this.loadingRoles = true;
      var excludeRoles = ["STUDENT", "APPLICANT", "PG-COORD", "HOD"];
      axios
        .get("admin/roles")
        .then((res) => {
          this.availableRoles = (res.data.data || [])
            .filter((r) => !excludeRoles.includes(r.role || r.name || r))
            .map((r) => ({
              text: r.role || r.name || r,
              value: r.id,
            }));
        })
        .catch(() => Toast.fire({ icon: "error", title: "Failed to load roles" }))
        .finally(() => (this.loadingRoles = false));
    },

    openApproveModal(item) {
      this.selectedRequest = item;
      this.selectedRoles = [];
      this.$bvModal.show("approve-modal");
    },

    approveRequest(evt) {
      evt.preventDefault();
      this.approving = true;
      axios
        .patch("admin/access-requests/" + this.selectedRequest.id + "/approve", {
          roles: this.selectedRoles,
        })
        .then(() => {
          Toast.fire({ icon: "success", title: "Access request approved" });
          this.$bvModal.hide("approve-modal");
          this.loadAccessRequests();
          this.loadAdminUsers();
        })
        .catch((err) => {
          let msg = err.response && err.response.data && err.response.data.error
            ? err.response.data.error
            : "Failed to approve request";
          Toast.fire({ icon: "error", title: msg });
        })
        .finally(() => (this.approving = false));
    },

    denyRequest(item) {
      Swal.fire({
        title: "Deny Access Request?",
        text: "Deny access for " + item.email + "?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, deny",
        confirmButtonColor: "#d33",
      }).then((result) => {
        if (result.isConfirmed) {
          axios
            .patch("admin/access-requests/" + item.id + "/deny")
            .then(() => {
              Toast.fire({ icon: "success", title: "Access request denied" });
              this.loadAccessRequests();
            })
            .catch(() => Toast.fire({ icon: "error", title: "Failed to deny request" }));
        }
      });
    },

    deleteRequest(item) {
      Swal.fire({
        title: "Delete Request?",
        text: "Delete access request from " + item.email + "? This cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete",
        confirmButtonColor: "#d33",
      }).then((result) => {
        if (result.isConfirmed) {
          axios
            .delete("admin/access-requests/" + item.id)
            .then(() => {
              Toast.fire({ icon: "success", title: "Request deleted" });
              this.loadAccessRequests();
            })
            .catch(() => Toast.fire({ icon: "error", title: "Failed to delete request" }));
        }
      });
    },

    openEditRolesModal(item) {
      this.selectedUser = item;
      this.editRoles = (item.roles || []).map((roleName) => {
        let found = this.availableRoles.find((r) => r.text === roleName);
        return found ? found.value : null;
      }).filter(Boolean);
      this.$bvModal.show("edit-roles-modal");
    },

    updateUserRoles(evt) {
      evt.preventDefault();
      this.updatingRoles = true;
      axios
        .put("admin/admin-users/" + this.selectedUser.id + "/roles", {
          roles: this.editRoles,
        })
        .then(() => {
          Toast.fire({ icon: "success", title: "Roles updated" });
          this.$bvModal.hide("edit-roles-modal");
          this.loadAdminUsers();
        })
        .catch((err) => {
          let msg = err.response && err.response.data && err.response.data.error
            ? err.response.data.error
            : "Failed to update roles";
          Toast.fire({ icon: "error", title: msg });
        })
        .finally(() => (this.updatingRoles = false));
    },

    deleteAdminUser(item) {
      Swal.fire({
        title: "Delete Admin User?",
        text: "Remove " + item.email + " from the admin system? This cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete",
        confirmButtonColor: "#d33",
      }).then((result) => {
        if (result.isConfirmed) {
          axios
            .delete("admin/admin-users/" + item.id)
            .then(() => {
              Toast.fire({ icon: "success", title: "Admin user deleted" });
              this.loadAdminUsers();
            })
            .catch((err) => {
              let msg = err.response && err.response.data && err.response.data.error
                ? err.response.data.error
                : "Failed to delete user";
              Toast.fire({ icon: "error", title: msg });
            });
        }
      });
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      let d = new Date(dateStr);
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    },
  },
};
</script>
